import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { useAppContext } from '../lib/store';
import { WorkOrder, WorkOrderLaborItem, WorkOrderMaterialItem } from '../lib/types';
import { Plus, Trash2, GripVertical, FileDown, Save, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
export function WorkOrderFormPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    workOrders,
    customers,
    users,
    addWorkOrder,
    updateWorkOrder,
    generateWorkOrderNumber
  } = useAppContext();
  const isEdit = !!id;
  const existingWo = isEdit ? workOrders.find(wo => wo.id === id) : null;
  // Form State
  const [formData, setFormData] = useState<Partial<WorkOrder>>({
    customer_id: '',
    execution_date: new Date().toISOString().split('T')[0],
    location: '',
    customer_reference_or_orderer: '',
    urgency: 'Redno',
    status: 'Osnutek',
    assigned_user_id: '',
    work_description: '',
    notes: '',
    technician_name: '',
    orderer_name: ''
  });
  const [laborItems, setLaborItems] = useState<Partial<WorkOrderLaborItem>[]>([{
    id: 'temp_1',
    description: '',
    quantity: 1,
    unit: 'h'
  }]);
  const [materialItems, setMaterialItems] = useState<Partial<WorkOrderMaterialItem>[]>([{
    id: 'temp_1',
    material_name: '',
    quantity: 1,
    unit: 'kos'
  }]);
  const [previewNumber, setPreviewNumber] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [openSelect, setOpenSelect] = useState<string | null>(null);
  const selectOpen = (name: string) => openSelect === name;
  const onSelectOpenChange = (name: string) => (isOpen: boolean) => {
    setOpenSelect(isOpen ? name : null);
  };
  useEffect(() => {
    if (existingWo) {
      setFormData(existingWo);
      if (existingWo.labor_items.length > 0) setLaborItems(existingWo.labor_items);
      if (existingWo.material_items.length > 0) setMaterialItems(existingWo.material_items);
      setPreviewNumber(existingWo.work_order_number);
    }
  }, [existingWo]);
  // Generate preview number when customer changes (only for new orders)
  useEffect(() => {
    if (!isEdit && formData.customer_id) {
      const currentYear = new Date().getFullYear();
      const {
        number
      } = generateWorkOrderNumber(formData.customer_id, currentYear);
      setPreviewNumber(number);
    } else if (!isEdit) {
      setPreviewNumber('');
    }
  }, [formData.customer_id, isEdit]);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.customer_id) newErrors.customer_id = 'Izbira stranke je obvezna';
    if (!formData.execution_date) newErrors.execution_date = 'Datum izvedbe je obvezen';
    if (!formData.location?.trim()) newErrors.location = 'Lokacija je obvezna';
    // Check if at least one description or item exists
    const hasLabor = laborItems.some(item => item.description?.trim());
    const hasMaterial = materialItems.some(item => item.material_name?.trim());
    if (!formData.work_description?.trim() && !hasLabor && !hasMaterial) {
      newErrors.general = 'Vnesite opis opravila ali dodajte vsaj eno postavko dela/materiala';
      toast.error('Manjkajoči podatki', {
        description: newErrors.general
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const prepareDataForSave = () => {
    // Filter out empty rows
    const cleanedLabor = laborItems.filter(item => item.description?.trim()).map((item, index) => ({
      ...item,
      id: item.id?.startsWith('temp_') ? `l_${Date.now()}_${index}` : item.id,
      sort_order: index + 1
    })) as WorkOrderLaborItem[];
    const cleanedMaterial = materialItems.filter(item => item.material_name?.trim()).map((item, index) => ({
      ...item,
      id: item.id?.startsWith('temp_') ? `m_${Date.now()}_${index}` : item.id,
      sort_order: index + 1
    })) as WorkOrderMaterialItem[];
    return {
      ...formData,
      labor_items: cleanedLabor,
      material_items: cleanedMaterial
    };
  };
  const handleSave = (generatePdf = false, complete = false) => {
    if (!validate()) return;
    const dataToSave = prepareDataForSave();
    if (complete) {
      dataToSave.status = 'Zaključeno';
    }
    if (isEdit && id) {
      updateWorkOrder(id, dataToSave);
      if (generatePdf) {
        toast.success('PDF dokument se generira...');
        // In a real app, this would trigger PDF generation
      }
      navigate(`/nalogi/${id}`);
    } else {
      addWorkOrder(dataToSave as any);
      if (generatePdf) {
        toast.success('PDF dokument se generira...');
      }
      navigate('/nalogi');
    }
  };
  const handleChange = (field: keyof WorkOrder, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  // Repeater Handlers
  const addLaborRow = () => {
    setLaborItems([...laborItems, {
      id: `temp_${Date.now()}`,
      description: '',
      quantity: 1,
      unit: 'h'
    }]);
  };
  const removeLaborRow = (index: number) => {
    setLaborItems(laborItems.filter((_, i) => i !== index));
  };
  const updateLaborRow = (index: number, field: keyof WorkOrderLaborItem, value: any) => {
    const newItems = [...laborItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setLaborItems(newItems);
  };
  const addMaterialRow = () => {
    setMaterialItems([...materialItems, {
      id: `temp_${Date.now()}`,
      material_name: '',
      quantity: 1,
      unit: 'kos'
    }]);
  };
  const removeMaterialRow = (index: number) => {
    setMaterialItems(materialItems.filter((_, i) => i !== index));
  };
  const updateMaterialRow = (index: number, field: keyof WorkOrderMaterialItem, value: any) => {
    const newItems = [...materialItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setMaterialItems(newItems);
  };
  return <div className="max-w-5xl mx-auto space-y-6 pb-24" data-id="element-552">
      <PageHeader title={isEdit ? 'Urejanje delovnega naloga' : 'Nov delovni nalog'} description={previewNumber ? `Številka naloga: ${previewNumber}` : 'Izberite stranko za dodelitev številke.'} data-id="element-553" />

      <div className="space-y-6" data-id="element-554">
        {/* General Info */}
        <Card data-id="element-555">
          <CardContent className="p-6 space-y-6" data-id="element-556">
            <h3 className="text-lg font-medium border-b pb-2" data-id="element-557">
              Splošni podatki
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-558">
              <div className="space-y-2" data-id="element-559">
                <Label className={errors.customer_id ? 'text-red-500' : ''} data-id="element-560">
                  Stranka *
                </Label>
                <Select value={formData.customer_id} onValueChange={val => handleChange('customer_id', val)} disabled={isEdit} open={selectOpen('customer')} onOpenChange={onSelectOpenChange('customer')} data-id="element-561">
                  <SelectTrigger className={errors.customer_id ? 'border-red-500' : ''} data-id="element-562">
                    <span className={formData.customer_id ? '' : 'text-muted-foreground'} data-id="element-563">
                      {formData.customer_id ? customers.find(c => c.id === formData.customer_id)?.name : 'Izberite stranko'}
                    </span>
                  </SelectTrigger>
                  <SelectContent data-id="element-564">
                    {customers.filter(c => c.is_active || c.id === formData.customer_id).map(c => <SelectItem key={c.id} value={c.id} data-id="element-565">
                          {c.name}
                        </SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.customer_id && <p className="text-sm text-red-500" data-id="element-566">{errors.customer_id}</p>}
              </div>

              <div className="space-y-2" data-id="element-567">
                <Label className={errors.execution_date ? 'text-red-500' : ''} data-id="element-568">
                  Datum izvedbe *
                </Label>
                <Input type="date" value={formData.execution_date} onChange={e => handleChange('execution_date', e.target.value)} className={errors.execution_date ? 'border-red-500' : ''} data-id="element-569" />
                {errors.execution_date && <p className="text-sm text-red-500" data-id="element-570">
                    {errors.execution_date}
                  </p>}
              </div>

              <div className="space-y-2 lg:col-span-1" data-id="element-571">
                <Label className={errors.location ? 'text-red-500' : ''} data-id="element-572">
                  Lokacija *
                </Label>
                <Input value={formData.location} onChange={e => handleChange('location', e.target.value)} placeholder="Npr. Poslovalnica Center" className={errors.location ? 'border-red-500' : ''} data-id="element-573" />
                {errors.location && <p className="text-sm text-red-500" data-id="element-574">{errors.location}</p>}
              </div>

              <div className="space-y-2" data-id="element-575">
                <Label data-id="element-576">Stopnja nujnosti</Label>
                <Select value={formData.urgency} onValueChange={val => handleChange('urgency', val)} open={selectOpen('urgency')} onOpenChange={onSelectOpenChange('urgency')} data-id="element-577">
                  <SelectTrigger data-id="element-578">
                    <span className={formData.urgency ? '' : 'text-muted-foreground'} data-id="element-579">
                      {formData.urgency || 'Izberite nujnost'}
                    </span>
                  </SelectTrigger>
                  <SelectContent data-id="element-580">
                    <SelectItem value="Redno" data-id="element-581">Redno</SelectItem>
                    <SelectItem value="Nujno" data-id="element-582">Nujno</SelectItem>
                    <SelectItem value="Kritično" data-id="element-583">Kritično</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2" data-id="element-584">
                <Label data-id="element-585">Št. / Naročnik</Label>
                <Input value={formData.customer_reference_or_orderer} onChange={e => handleChange('customer_reference_or_orderer', e.target.value)} placeholder="Ime naročnika ali št. naročilnice" data-id="element-586" />
              </div>

              <div className="space-y-2" data-id="element-587">
                <Label data-id="element-588">Izvajalec</Label>
                <Select value={formData.assigned_user_id} onValueChange={val => handleChange('assigned_user_id', val)} open={selectOpen('user')} onOpenChange={onSelectOpenChange('user')} data-id="element-589">
                  <SelectTrigger data-id="element-590">
                    <span className={formData.assigned_user_id ? '' : 'text-muted-foreground'} data-id="element-591">
                      {formData.assigned_user_id ? users.find(u => u.id === formData.assigned_user_id)?.full_name : 'Izberite izvajalca'}
                    </span>
                  </SelectTrigger>
                  <SelectContent data-id="element-592">
                    {users.filter(u => u.is_active && (u.role === 'Technician' || u.role === 'Admin')).map(u => <SelectItem key={u.id} value={u.id} data-id="element-593">
                          {u.full_name}
                        </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 pt-4" data-id="element-594">
              <Label data-id="element-595">Opis opravila</Label>
              <Textarea value={formData.work_description} onChange={e => handleChange('work_description', e.target.value)} rows={3} placeholder="Podroben opis napake ali zahtevanega dela..." data-id="element-596" />
            </div>
          </CardContent>
        </Card>

        {/* Labor Repeater */}
        <Card data-id="element-597">
          <CardContent className="p-6 space-y-4" data-id="element-598">
            <div className="flex items-center justify-between border-b pb-2" data-id="element-599">
              <h3 className="text-lg font-medium" data-id="element-600">Opravljeno delo</h3>
              <Button type="button" variant="outline" size="sm" onClick={addLaborRow} data-id="element-601">
                <Plus className="w-4 h-4 mr-2" data-id="element-602" /> Dodaj vrstico
              </Button>
            </div>

            <div className="space-y-4" data-id="element-603">
              {laborItems.map((item, index) => <div key={item.id} className="group border rounded-lg p-3 md:p-0 md:border-0 md:rounded-none" data-id="element-604">
                  <div className="flex items-start gap-2 md:gap-3" data-id="element-605">
                    <div className="hidden md:block mt-2 text-slate-300 cursor-move" data-id="element-606">
                      <GripVertical className="w-5 h-5" data-id="element-607" />
                    </div>
                    <div className="flex-1 space-y-2 md:space-y-0 md:flex md:items-start md:gap-3" data-id="element-608">
                      <div className="flex-1" data-id="element-609">
                        <Input placeholder="Opis opravljenega dela" value={item.description} onChange={e => updateLaborRow(index, 'description', e.target.value)} data-id="element-610" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 md:flex md:gap-3" data-id="element-611">
                        <div className="md:w-24" data-id="element-612">
                          <Input type="number" min="0" step="0.5" placeholder="Količina" value={item.quantity} onChange={e => updateLaborRow(index, 'quantity', parseFloat(e.target.value) || 0)} data-id="element-613" />
                        </div>
                        <div className="md:w-24" data-id="element-614">
                          <Input placeholder="EM (npr. h)" value={item.unit} onChange={e => updateLaborRow(index, 'unit', e.target.value)} data-id="element-615" />
                        </div>
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="text-slate-400 hover:text-red-500 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0" onClick={() => removeLaborRow(index)} data-id="element-616">
                      <Trash2 className="w-4 h-4" data-id="element-617" />
                    </Button>
                  </div>
                </div>)}
              {laborItems.length === 0 && <p className="text-sm text-slate-500 italic py-2" data-id="element-618">
                  Ni dodanih postavk za delo.
                </p>}
            </div>
          </CardContent>
        </Card>

        {/* Material Repeater */}
        <Card data-id="element-619">
          <CardContent className="p-6 space-y-4" data-id="element-620">
            <div className="flex items-center justify-between border-b pb-2" data-id="element-621">
              <h3 className="text-lg font-medium" data-id="element-622">Porabljen material</h3>
              <Button type="button" variant="outline" size="sm" onClick={addMaterialRow} data-id="element-623">
                <Plus className="w-4 h-4 mr-2" data-id="element-624" /> Dodaj vrstico
              </Button>
            </div>

            <div className="space-y-4" data-id="element-625">
              {materialItems.map((item, index) => <div key={item.id} className="group border rounded-lg p-3 md:p-0 md:border-0 md:rounded-none" data-id="element-626">
                  <div className="flex items-start gap-2 md:gap-3" data-id="element-627">
                    <div className="hidden md:block mt-2 text-slate-300 cursor-move" data-id="element-628">
                      <GripVertical className="w-5 h-5" data-id="element-629" />
                    </div>
                    <div className="flex-1 space-y-2 md:space-y-0 md:flex md:items-start md:gap-3" data-id="element-630">
                      <div className="flex-1" data-id="element-631">
                        <Input placeholder="Naziv materiala" value={item.material_name} onChange={e => updateMaterialRow(index, 'material_name', e.target.value)} data-id="element-632" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 md:flex md:gap-3" data-id="element-633">
                        <div className="md:w-24" data-id="element-634">
                          <Input type="number" min="0" step="0.5" placeholder="Količina" value={item.quantity} onChange={e => updateMaterialRow(index, 'quantity', parseFloat(e.target.value) || 0)} data-id="element-635" />
                        </div>
                        <div className="md:w-24" data-id="element-636">
                          <Input placeholder="EM (npr. kos)" value={item.unit} onChange={e => updateMaterialRow(index, 'unit', e.target.value)} data-id="element-637" />
                        </div>
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" className="text-slate-400 hover:text-red-500 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0" onClick={() => removeMaterialRow(index)} data-id="element-638">
                      <Trash2 className="w-4 h-4" data-id="element-639" />
                    </Button>
                  </div>
                </div>)}
              {materialItems.length === 0 && <p className="text-sm text-slate-500 italic py-2" data-id="element-640">
                  Ni dodanih postavk za material.
                </p>}
            </div>
          </CardContent>
        </Card>

        {/* Sign-off & Notes */}
        <Card data-id="element-641">
          <CardContent className="p-6 space-y-6" data-id="element-642">
            <div className="space-y-2" data-id="element-643">
              <Label data-id="element-644">Opombe</Label>
              <Textarea value={formData.notes} onChange={e => handleChange('notes', e.target.value)} rows={2} data-id="element-645" />
            </div>

            <Separator data-id="element-646" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-647">
              <div className="space-y-2" data-id="element-648">
                <Label data-id="element-649">Izvajalec (Ime in priimek)</Label>
                <Input value={formData.technician_name} onChange={e => handleChange('technician_name', e.target.value)} placeholder="Podpisnik izvajalca" data-id="element-650" />
              </div>
              <div className="space-y-2" data-id="element-651">
                <Label data-id="element-652">Naročnik (Ime in priimek)</Label>
                <Input value={formData.orderer_name} onChange={e => handleChange('orderer_name', e.target.value)} placeholder="Podpisnik stranke" data-id="element-653" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sticky Action Bar */}
      <div className="fixed bottom-0 left-0 md:left-64 right-0 p-4 bg-white border-t shadow-[0_-10px_15px_-3px_rgba(0,0,0,0.05)] flex items-center justify-end gap-3 z-20" data-id="element-654">
        <Button type="button" variant="outline" onClick={() => navigate('/nalogi')} data-id="element-655">
          Prekliči
        </Button>
        <Button type="button" onClick={() => handleSave(false)} data-id="element-656">
          <Save className="w-4 h-4 mr-2" data-id="element-657" />
          Shrani
        </Button>
      </div>
    </div>;
}